import { useState } from "react";
import { Navigate } from "react-router-dom";
import {
  Box,
  Typography,
  Tab,
  Tabs,
  CircularProgress,
} from "@mui/material";
import AdminPanelSettingsRoundedIcon from "@mui/icons-material/AdminPanelSettingsRounded";
import { useIsAdmin } from "../../hooks/useIsAdmin";
import AdminPlansPage from "./AdminPlansPage";
import AdminBusinessesPage from "./AdminBusinessesPage";
import AdminFeaturesPage from "./AdminFeaturesPage";
import AdminVoiceNumbersPage from "./AdminVoiceNumbersPage";

type AdminTab = "plans" | "businesses" | "features" | "voiceNumbers";

export default function AdminPage() {
  const { isAdmin, loading } = useIsAdmin();
  const [tab, setTab] = useState<AdminTab>("plans");

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 10 }}>
        <CircularProgress sx={{ color: "#C9734F" }} />
      </Box>
    );
  }

  if (!isAdmin) {
    return <Navigate to="/" replace />;
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1 }}>
        <AdminPanelSettingsRoundedIcon sx={{ color: "#C9734F", fontSize: 28 }} />
        <Typography variant="h5" sx={{ fontWeight: 700, color: "#0F172A" }}>
          Platform Admin
        </Typography>
      </Box>

      <Typography sx={{ color: "#64748B", fontSize: "0.85rem", mb: 3 }}>
        Manage subscription plans, business entitlements, and feature flags.
      </Typography>

      {/* Tabs */}
      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v as AdminTab)}
        sx={{
          mb: 3,
          borderBottom: "1px solid #E2E8F0",
          "& .MuiTab-root": {
            textTransform: "none",
            fontWeight: 600,
            fontSize: "0.875rem",
            color: "#64748B",
            minHeight: 44,
            px: 0,
            mr: 3,
            "&.Mui-selected": { color: "#C9734F" },
          },
          "& .MuiTabs-indicator": { backgroundColor: "#C9734F" },
        }}
      >
        <Tab label="Plans" value="plans" />
        <Tab label="Businesses" value="businesses" />
        <Tab label="Features" value="features" />
        <Tab label="Voice Numbers" value="voiceNumbers" />
      </Tabs>

      {tab === "plans" && <AdminPlansPage />}
      {tab === "businesses" && <AdminBusinessesPage />}
      {tab === "features" && <AdminFeaturesPage />}
      {tab === "voiceNumbers" && <AdminVoiceNumbersPage />}
    </Box>
  );
}
